'use client';

import { useRef } from 'react';
import { faLocationDot } from '@fortawesome/pro-light-svg-icons';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { SuggestionSection } from './suggestion-section.component';

import { Icon } from '@/components/ui/icon.component';
import { Button } from '@/components/ui/button/button.component';
import { SearchCardComponent } from '@/app/(main)/(pages)/busca/[slug]/_components/search-schedules-list/search-card/search-card.component';
import {
  NearCitySchedules,
  hasRenderableNearCitySuggestions,
} from '@/app/(main)/(pages)/busca/[slug]/_services/get-near-city-schedules.service';
import { useNearCityExpansion } from '@/app/(main)/(pages)/busca/[slug]/_hooks/use-near-city-expansion.hook';
import { CodesOrSlugsResponse } from '@/app/(main)/(pages)/busca/[slug]/_types/codes-or-slugs-response.type';
import { SearchPartnerData } from '@/app/(main)/(pages)/busca/[slug]/_types/search-partner-data.type';
import { getAnalyticsPlugin } from '@/app/(main)/_components/logger/analytics/get-analytics-plugin';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';
import { useUserLocation } from '@/hooks/use-user-location.hook';

interface NearCitySuggestionProps {
  nearCitySchedules: NearCitySchedules | null;
  searchType: string;
  proceduresResearched: CodesOrSlugsResponse;
}

export function NearCitySuggestion({
  nearCitySchedules,
  searchType,
  proceduresResearched,
}: NearCitySuggestionProps) {
  const analytics = getAnalyticsPlugin();
  const { userLocation } = useUserLocation();
  const trackedCardsRef = useRef(new Set<string>());
  const { schedules, hasMore, isExpanded, isLoading, expand } = useNearCityExpansion({
    nearCitySchedules,
    searchType,
    procedureCodes: proceduresResearched.map((procedure) => procedure.code),
  });

  if (!hasRenderableNearCitySuggestions(nearCitySchedules)) {
    return null;
  }

  const [featured, ...remaining] = schedules;
  const isCollapsedWithMore = !isExpanded && hasMore;

  const trackCardClick = (schedule: SearchPartnerData) => {
    const cardId = `${schedule.partner.id}-${schedule.professional?.id ?? ''}`;

    if (trackedCardsRef.current.has(cardId)) {
      return;
    }

    trackedCardsRef.current.add(cardId);
    analytics.track(AnalyticsEnum.SEARCH_SUGGESTION_NEAR_CITY_SELECTED, {
      city_destino: schedule.partner?.facility?.city,
      distance_km: schedule.distance ?? undefined,
    });
  };

  const handleSeeAll = () => {
    analytics.track(AnalyticsEnum.SEARCH_SUGGESTION_NEAR_CITY_SEE_ALL, {
      city_origem: userLocation?.city,
      qtd_cidades: nearCitySchedules?.cityCount,
    });

    expand();
  };

  const renderCard = (schedule: SearchPartnerData) => (
    <SearchCardComponent
      fluid
      schedule={schedule}
      searchType={searchType}
      proceduresResearched={proceduresResearched}
    />
  );

  const renderTrackableCard = (schedule: SearchPartnerData, key?: string) => (
    <div key={key} onClickCapture={() => trackCardClick(schedule)}>
      {renderCard(schedule)}
    </div>
  );

  return (
    <SuggestionSection
      icon={<Icon icon={faLocationDot} className="text-xl text-green-900" />}
      title="Profissionais atendem em cidades próximas"
      featuredCard={(
        <div onClickCapture={() => trackCardClick(featured)}>
          {renderCard(featured)}
        </div>
      )}
      previewCard={isCollapsedWithMore && remaining[0] ? renderCard(remaining[0]) : undefined}
      additionalCards={isCollapsedWithMore
        ? null
        : remaining.map((schedule, index) => renderTrackableCard(
            schedule,
            `${schedule.partner.id}-${schedule.professional?.id ?? 'no-professional'}-${index}`,
          ))}
      cta={hasMore
        ? (
            <Button
              type="button"
              color="success"
              className="shadow-none"
              data-testid={DataTestIdEnum.SEARCH_PAGE_SUGGESTION_NEAR_CITY_CTA}
              isLoading={isLoading}
              onClick={handleSeeAll}
            >
              Ver mais profissionais
            </Button>
          )
        : undefined}
    />
  );
}
